import React, { useCallback, useEffect, useMemo } from 'react';
import { Form, ReduxControls, ProgressIndicator, ErrorBanner } from 'rhombus-react';
import {
	CREATIVE_ASSET_TYPES
} from 'utils/constants';
import {
	IMPOSIUM_PROJECT_CREATION_METHOD
} from 'widgets/social2/constants/common';
import { useDispatch, useSelector } from 'react-redux';
import { getFormValues, Field, change as changeFormValue } from "redux-form";
import { noop } from "utils/common";
import { clearImposiumErrors, getImposiumProjects } from "reducers/ImposiumProjects";
import { clearCompositions } from "reducers/ImposiumCompositions";

import './ImposiumProjectSelector.css';

function ImposiumProjectSelector({ input, meta, ...props }) {
	const dispatch = useDispatch();

	function resetSelectedCompositions(form, adType, creativeValues){
		//reset composition for usual creatives
		dispatch(changeFormValue(form, `${adType}.selectedCompositionId`, null));
		//reset composition for each PAC
		const { placements } = creativeValues;
		if (placements){
			Object.keys(placements).forEach((name) => {
				dispatch(changeFormValue(form, `${adType}.placements.${name}.selectedCompositionId`, null));
			});
		}
		//reset composition for each card
		const { cards } = creativeValues;
		if (cards){
			cards.forEach((card, index) => {
				dispatch(changeFormValue(form, `${adType}.cards[${index}].selectedCompositionId`, null));
				dispatch(changeFormValue(form, `${adType}.cards[${index}].imposiumComposition.id`, null));
			});
		}
		dispatch(clearCompositions());
	}

	const inputProps = {
		...input,
		...props,
		meta,
	};

	const { name, onChange, onFocus, showPristineErrors } = input || {};
	const [adType] = name.split(".");
	const {
		doVersionsExist,
		creativeValues,
		projects,
		isLoadingProjects,
		isProjectsLoaded,
		errorObj,
		libraryId,
	} = useSelector((state) => {
		return {
			doVersionsExist: state.creativeDetails?.numberOfVersions > 1,
			creativeValues: getFormValues(meta.form)(state)?.[adType] || {},
			projects: state.imposiumProjects?.items,
			isLoadingProjects: state.imposiumProjects?.isLoading,
			isProjectsLoaded: state.imposiumProjects?.isLoaded,
			errorObj: state.imposiumProjects?.error || {},
			libraryId: state.libraryProperties?.id,
		};
	});

	const value = input.value?.creationMethod ? input.value : {
		creationMethod: IMPOSIUM_PROJECT_CREATION_METHOD.NEW_PROJECT,
	};

	const { disabled } = inputProps;

	const isRadioDisabled = disabled || doVersionsExist;
	const isCreativeDynamic = creativeValues?.isDynamic;
	const assetType = creativeValues?.assetType;
	const projectState = creativeValues?.imposiumProject
		|| creativeValues.cards && creativeValues.cards[0]?.imposiumProject
		|| value;
	const projectId = projectState?.id;
	const creationMethod = projectState?.creationMethod;

	useEffect(() => {
		if (!isProjectsLoaded && !isLoadingProjects && creationMethod === IMPOSIUM_PROJECT_CREATION_METHOD.EXISTING_PROJECT) {
			dispatch(clearImposiumErrors());
			dispatch(getImposiumProjects(libraryId));
		}
	}, [libraryId, creationMethod, isProjectsLoaded, isLoadingProjects]);

	useEffect(() => {
		if (projects?.length > 0 && projectId) {
			const currentProject = projects.find(proj => proj.id === projectId);
			if (currentProject) {
				dispatch(changeFormValue(meta.form, `${adType}.selectedStoryId`, {
					value: currentProject.id,
					label: currentProject.name,
				}));
			}
		}
	}, [projects, projectId]);

	useEffect(() => {
		if (value.creationMethod === IMPOSIUM_PROJECT_CREATION_METHOD.NEW_PROJECT && value.newProjectName) {
			dispatch(changeFormValue(meta.form, `${adType}.newProjectName`, value.newProjectName));
		}
	}, [value.creationMethod, value.newProjectName]);

	const projectOptions = useMemo(() => {
		return projects?.map(project => ({
			value: project.id,
			label: project.name,
		}));
	}, [projects]);

	const onCreationMethodChange = useCallback((e) => {
		const method = e.target.defaultValue;
		if (method === IMPOSIUM_PROJECT_CREATION_METHOD.NEW_PROJECT){
			dispatch(changeFormValue(meta.form, `${adType}.selectedStoryId`, null));
		} else if (method === IMPOSIUM_PROJECT_CREATION_METHOD.EXISTING_PROJECT){
			dispatch(changeFormValue(meta.form, `${adType}.newProjectName`, null));
		}
		resetSelectedCompositions(meta.form, adType, creativeValues);
		dispatch(clearCompositions());
		onChange({
			...value,
			creationMethod: method,
		});
	}, [onChange, value]);

	const onNewProjectNameChange = useCallback((newProjectName) => {
		onChange({
			...value,
			newProjectName,
		});
	}, [onChange, value]);

	const onStoryIdChange = useCallback((newValue) => {
		onChange({
			...value,
			id: newValue?.value,
			name: newValue?.label,
		});
		resetSelectedCompositions(meta.form, adType, creativeValues);
	}, [onChange, value]);

	const abortConditions = [
		!isCreativeDynamic,
		assetType !== CREATIVE_ASSET_TYPES.IMPOSIUM,
		creativeValues.currentCard && creativeValues.currentCard > 0,
	];
	if (abortConditions.some(item => item)) {
		return null;
	}

	return (
		<div className="full-width imposium-project-selector-wrapper">
			<div className="full-width imposium-project-selector">
				<ProgressIndicator isLoading={isLoadingProjects} />
				{!!Object.keys(errorObj)?.length && (
					<ErrorBanner
						title="Sorry, please fix the errors below and try again."
						messages={[errorObj.description]}
					/>
				)}
				<span className="creation-method-header">Creation method:</span>
				<div className="new-project-radio">
					<Form.Checkbox
						label="New Imposium Project"
						type="radio"
						name="creationMethod"
						disabled={isRadioDisabled}
						value={IMPOSIUM_PROJECT_CREATION_METHOD.NEW_PROJECT}
						onChange={onCreationMethodChange}
						onFocus={onFocus}
						checked={creationMethod === IMPOSIUM_PROJECT_CREATION_METHOD.NEW_PROJECT}
					/>
				</div>
				<div className="ex-project-radio">
					<Form.Checkbox
						label="Select Existing Imposium Project"
						type="radio"
						name="creationMethod"
						disabled={isRadioDisabled}
						value={IMPOSIUM_PROJECT_CREATION_METHOD.EXISTING_PROJECT}
						onChange={onCreationMethodChange}
						onFocus={onFocus}
						checked={creationMethod === IMPOSIUM_PROJECT_CREATION_METHOD.EXISTING_PROJECT}
					/>
				</div>
			</div>
			{creationMethod === IMPOSIUM_PROJECT_CREATION_METHOD.NEW_PROJECT && (
				<Field
					label="Imposium Project Name"
					name="newProjectName"
					required
					className="full-width"
					component={ReduxControls.Input}
					type="text"
					placeholder="Enter Imposium project name"
					disabled={isRadioDisabled}
					onChange={onNewProjectNameChange}
					onFocus={onFocus}
					value={value.newProjectName}
					showPristineErrors={!!showPristineErrors}
				/>
			)
			}
			{creationMethod === IMPOSIUM_PROJECT_CREATION_METHOD.EXISTING_PROJECT && (
				<Field
					name="selectedStoryId"
					label="Imposium Project"
					placeholder="Select your Existing Project"
					component={ReduxControls.Select}
					icon="search"
					options={projectOptions}
					disabled={isRadioDisabled}
					isLoading={isLoadingProjects}
					isClearable={false}
					onMenuScrollToBottom={noop}
					showIcon
					onChange={onStoryIdChange}
					onFocus={onFocus}
					value={value.id}
					showPristineErrors={!!showPristineErrors}
				/>
			)
			}
		</div>
	);
}
ImposiumProjectSelector.displayName = "ImposiumProjectSelector";

export const imposiumProject = {
	name: "imposiumProject",
	required: true,
	component: ImposiumProjectSelector,
};
